/**
 * Per-user "Learn Medsender" tutorial progress.
 *
 * Progress is intentionally client-side only: PR 1 added a single
 * `users.has_completed_tutorial` boolean and this feature is explicitly not
 * allowed to add migrations, so per-tutorial granularity lives in
 * localStorage. The backend boolean is the "all done / don't show again"
 * summary flag that suppresses the post-walkthrough auto-open.
 *
 * The store is namespaced by user so that two accounts sharing a browser do
 * not inherit each other's checkmarks. The frontend never receives a numeric
 * user id (see User#object in app/models/user.rb — it exposes `email`, and
 * auth state exposes it as `uid`), so the email is the only stable per-user
 * identifier available on the client. It is the same value already persisted
 * under `m8_uid`, so this introduces no new class of stored data.
 */

export const TUTORIAL_STORAGE_KEY = 'm8_tutorial_progress_v1';
const LEGACY_TUTORIAL_STORAGE_KEY = 'm8_tutorial_progress';

/**
 * The keys a logout should PRESERVE, owned here rather than by the shared clear
 * helper. `actions/auth.js#resetAuth` passes this as `preserveKeys`.
 *
 * These record what the user has LEARNED, not who they are or what they may
 * access: signing out is not "forget my training". Both are namespaced per user
 * email INTERNALLY, which is what makes keeping them safe — the legacy key is
 * included so its one-time copy-forward in `readStore` can still run for a user
 * who logs out before it happens.
 */
export const PRESERVED_LEARNING_KEYS = new Set([
  TUTORIAL_STORAGE_KEY,
  LEGACY_TUTORIAL_STORAGE_KEY,
]);

const ANONYMOUS_USER_KEY = 'anonymous';

/** Normalizes a user identifier (email/uid) into a stable storage namespace. */
export const resolveUserKey = (identifier) => {
  if (typeof identifier !== 'string') return ANONYMOUS_USER_KEY;
  const trimmed = identifier.trim().toLowerCase();
  return trimmed || ANONYMOUS_USER_KEY;
};

/**
 * The signed-in user's email, as the app actually stores it.
 *
 * This is the PREFERRED identity source for the tutorial namespace, ahead of
 * `state.userProfile.email`. That reducer field IS assigned — by
 * `FINISHED_GET_USER_INFO`'s spread of the `/api/v1/users` body — but only on the
 * routes that dispatch `getUserInfo()`, and (until it gained a logout reset) it
 * outlived a sign-out, so after an in-app account switch it named the PREVIOUS
 * user. `m8_uid` is rewritten by every login path and cleared on logout, so it
 * cannot name the wrong person. It is read the same way in PSPDFViewer, CreateTask
 * and friends.
 *
 * An earlier version of this comment claimed the reducer field was "never assigned
 * by ANY reducer case". That was wrong, and it is what justified deriving the
 * profile-loaded gate from this key — which then let the auto-open latch be spent
 * before `has_completed_tutorial` had been fetched.
 */
export const readStoredUserEmail = () => {
  try {
    return localStorage.getItem('m8_uid') || '';
  } catch {
    return '';
  }
};

const emptyProgress = () => ({ completed: [], dismissed: false });

const parseStore = (raw) => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    // Guard against a legacy/foreign value of the wrong shape.
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const readStore = () => {
  try {
    const versionedStore = parseStore(localStorage.getItem(TUTORIAL_STORAGE_KEY));
    if (versionedStore) return versionedStore;

    // The versioned key was introduced after the first PR iteration. Copy the
    // old store forward once so existing users keep their completion state.
    const legacyStore = parseStore(localStorage.getItem(LEGACY_TUTORIAL_STORAGE_KEY)) || {};
    if (Object.keys(legacyStore).length > 0) {
      try {
        localStorage.setItem(TUTORIAL_STORAGE_KEY, JSON.stringify(legacyStore));
      } catch {
        // Storage may be read-only; the in-memory legacy value is still usable.
      }
    }
    return legacyStore;
  } catch {
    // Corrupt JSON or a browser that denies storage access: behave as if the
    // user has no progress rather than breaking the menu.
    return {};
  }
};

const writeStore = (store) => {
  try {
    localStorage.setItem(TUTORIAL_STORAGE_KEY, JSON.stringify(store));
    return true;
  } catch {
    return false;
  }
};

const normalizeEntry = (entry) => {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return emptyProgress();
  const completed = Array.isArray(entry.completed)
    ? entry.completed.filter((id) => typeof id === 'string' && id)
    : [];
  return {
    // De-duplicate so repeated completions can't grow the array unbounded.
    completed: [...new Set(completed)],
    dismissed: entry.dismissed === true,
  };
};

/** Returns `{ completed: string[], dismissed: boolean }` for one user. */
export const readProgress = (identifier) => {
  const store = readStore();
  return normalizeEntry(store[resolveUserKey(identifier)]);
};

const persistProgress = (identifier, progress) => {
  const userKey = resolveUserKey(identifier);
  // Never write to the shared `anonymous` bucket.
  //
  // That namespace is not a per-user store, it is a browser-wide one — and because
  // the progress key now survives logout, anything written there became permanent
  // cross-user state: whoever next signs in with an unresolvable identity reads
  // someone else's checkmarks. An unknown identity means we cannot attribute the
  // progress, so the honest outcome is to keep it in memory only. The caller still
  // gets the updated value, so the UI is correct for this session.
  if (userKey === ANONYMOUS_USER_KEY) return { ...progress, persisted: false };
  const store = readStore();
  const next = { ...store, [userKey]: progress };
  // `persisted` tells the caller whether the write actually landed. It used to
  // discard `writeStore`'s result, so a denied or full localStorage looked exactly
  // like a successful save — and the durable `has_completed_tutorial` flag could
  // then be PATCHed on the strength of state that would be gone on the next load.
  // Additive: consumers read `completed`/`dismissed` and are unaffected.
  return { ...progress, persisted: writeStore(next) };
};

/** Marks one tutorial complete and returns the updated progress. */
export const markTutorialComplete = (identifier, tutorialId) => {
  const current = readProgress(identifier);
  // Already recorded (or nothing to record): the stored state already says this, so
  // report it as persisted rather than leaving `persisted` undefined.
  if (!tutorialId || current.completed.includes(tutorialId)) return { ...current, persisted: true };
  return persistProgress(identifier, {
    ...current,
    completed: [...current.completed, tutorialId],
  });
};

/** Records an explicit "don't show again" and returns the updated progress. */
export const markTutorialsDismissed = (identifier) => {
  const current = readProgress(identifier);
  if (current.dismissed) return { ...current, persisted: true };
  return persistProgress(identifier, { ...current, dismissed: true });
};

/**
 * Clears one user's progress.
 *
 * No UI calls this. It is kept as the single supported way to un-complete a
 * tutorial — the checklist deliberately has no "Start over" control (its docstring
 * used to claim one existed, which was simply false) — and it is what a future
 * reset affordance or a support tool should go through rather than reaching into
 * localStorage directly.
 */
export const resetProgress = (identifier) => {
  const cleared = emptyProgress();
  persistProgress(identifier, cleared);
  return cleared;
};

/* ------------------------------------------------- surviving a page teardown */

/**
 * Key for the "a tutorial was running when the page went away" marker.
 *
 * Deliberately sessionStorage, not localStorage: this is per-tab and must not
 * outlive the tab. A stale marker in localStorage would relaunch a tutorial days
 * later in an unrelated session.
 */
export const TUTORIAL_SESSION_KEY = 'm8_tutorial_session_v1';

/**
 * Records that `tutorialId` is running, so it can be picked back up if the page
 * is torn down underneath it.
 *
 * Several taught actions destroy the JS context: archiving calls
 * `window.location.reload()` (UploadedRecords) and a successful upload calls
 * `window.open(..., '_self')`. Redux — and with it `activeTutorialId` and
 * `menuOpen` — does not survive either, so without this marker the tutorial and
 * the checklist simply disappear mid-lesson.
 */
export const rememberTutorialSession = (tutorialId, { resumes = 0 } = {}) => {
  if (!tutorialId) return;
  try {
    sessionStorage.setItem(TUTORIAL_SESSION_KEY, JSON.stringify({ tutorialId, resumes }));
  } catch {
    // Storage denied/full: resuming is a nicety, never a requirement.
  }
};

/**
 * How many times one tutorial may be auto-resumed before we stop and just show
 * the checklist.
 *
 * Clearing the marker on read is not enough on its own: a resumed tutorial
 * re-arms the marker (it is running again, and should survive a further
 * teardown), so a screen that reloads on load could otherwise resume forever.
 */
export const MAX_TUTORIAL_RESUMES = 2;

/** Key for the "a tutorial just finished; reopen the checklist" marker. */
export const TUTORIAL_COMPLETED_KEY = 'm8_tutorial_completed_v1';

/**
 * How long a just-completed marker stays honourable, in ms.
 *
 * The reload we are covering for happens within about a second of the click
 * (UploadedRecords reloads 800ms after archiving). A window keeps a marker from
 * lingering and then popping the checklist open on some unrelated page load much
 * later — the in-app case has already reopened it directly by then.
 */
export const COMPLETION_REOPEN_WINDOW_MS = 15000;

/**
 * Records that `tutorialId` just completed, so the checklist can be reopened if
 * the page is torn down before the in-app reopen is visible.
 *
 * This must NOT simply clear the session marker: archiving completes the tutorial
 * and THEN reloads, so clearing on completion is precisely what left the user
 * with a bare page.
 */
export const rememberTutorialCompletion = (tutorialId, now = Date.now()) => {
  if (!tutorialId) return;
  try {
    sessionStorage.removeItem(TUTORIAL_SESSION_KEY);
    sessionStorage.setItem(TUTORIAL_COMPLETED_KEY, JSON.stringify({ tutorialId, completedAt: now }));
  } catch {
    // Reopening is a nicety, never a requirement.
  }
};

/** Reads and clears the just-completed marker, ignoring a stale or corrupt one. */
export const takeTutorialCompletion = (now = Date.now()) => {
  let raw = null;
  try {
    raw = sessionStorage.getItem(TUTORIAL_COMPLETED_KEY);
    sessionStorage.removeItem(TUTORIAL_COMPLETED_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.tutorialId !== 'string' || !parsed.tutorialId) return null;
    if (!Number.isFinite(parsed.completedAt)) return null;
    const age = now - parsed.completedAt;
    if (age < 0 || age > COMPLETION_REOPEN_WINDOW_MS) return null;
    return parsed.tutorialId;
  } catch {
    return null;
  }
};

export const clearTutorialSession = () => {
  try {
    sessionStorage.removeItem(TUTORIAL_SESSION_KEY);
    sessionStorage.removeItem(TUTORIAL_COMPLETED_KEY);
  } catch {
    // Nothing to do; a stale marker is cleared on read anyway.
  }
};

/**
 * Reads the marker AND clears it, returning `{ tutorialId, resumes }` or null.
 *
 * Clear-on-read means one page load can act on it at most once; `resumes` is what
 * bounds it across loads (see MAX_TUTORIAL_RESUMES).
 */
export const takeTutorialSession = () => {
  let raw = null;
  try {
    raw = sessionStorage.getItem(TUTORIAL_SESSION_KEY);
  } catch {
    return null;
  }
  clearTutorialSession();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.tutorialId !== 'string' || !parsed.tutorialId) return null;
    const resumes = Number.isInteger(parsed.resumes) && parsed.resumes >= 0 ? parsed.resumes : 0;
    return { tutorialId: parsed.tutorialId, resumes };
  } catch {
    return null;
  }
};

export const isTutorialComplete = (progress, tutorialId) => (
  !!progress && Array.isArray(progress.completed) && progress.completed.includes(tutorialId)
);

/**
 * True when every APPLICABLE tutorial in `tutorials` is complete.
 *
 * Callers pass the CORE list and a true result is persisted as the durable
 * `users.has_completed_tutorial` flag, so what counts as "applicable" matters.
 *
 * History, because this has now been both ways and the reasoning is the point:
 * it originally skipped `available === false` entries, which was wrong while
 * availability was derived from transient record COUNTS — a user could finish one
 * core tutorial while the rest were momentarily pre-locked, satisfy "all done", and
 * permanently suppress the auto-open. It was then changed to require every core id
 * unconditionally, which was right *because availability had become a hardcoded
 * `true`* and nothing could actually be unavailable.
 *
 * Availability is real again, and now reflects a stable ORG-level fact rather than a
 * transient one: `filters-search-labels` cannot run without the `inbox_redesign`
 * flag, because every one of its steps targets a redesign-only control. Requiring it
 * anyway made the durable flag unreachable for those orgs — an unfinishable
 * checklist that re-offers itself after every walkthrough, forever. So a
 * structurally-unavailable tutorial is treated as NOT APPLICABLE and skipped, and
 * `false` is returned when nothing is applicable at all, so an empty set can never
 * read as "done".
 */
export const areAllTutorialsComplete = (progress, tutorials = []) => {
  const applicable = tutorials.filter((tutorial) => tutorial && tutorial.available !== false);
  if (!applicable.length) return false;
  return applicable.every((tutorial) => isTutorialComplete(progress, tutorial.id));
};

/**
 * Decides whether the checklist should auto-open after the walkthrough.
 *
 * Auto-open happens at most once per user per browser: `has_completed_tutorial`
 * from the server suppresses it permanently, and a local `dismissed` flag or a
 * finished checklist suppresses it too. The help-menu entry is always
 * available regardless of what this returns.
 *
 * `walkthroughStartDestination` is the answer to the walkthrough's final "what would
 * you like to do first?" step (`users.walkthrough_start_destination`). Only an
 * explicit `'home'` suppresses the auto-open — the user asked to go straight to the
 * inbox and popping the checklist over it would ignore that. Every other value,
 * including `null`, opens it: `null` is what users who finished the walkthrough
 * before that step existed carry, and they must keep the original behaviour.
 */
export const shouldAutoOpenTutorialMenu = ({
  hasCompletedWalkthrough,
  hasCompletedTutorial,
  walkthroughStartDestination,
  progress,
  tutorials = [],
} = {}) => {
  if (!hasCompletedWalkthrough) return false;
  if (hasCompletedTutorial) return false;
  if (walkthroughStartDestination === 'home') return false;
  if (!progress || progress.dismissed) return false;
  if (areAllTutorialsComplete(progress, tutorials)) return false;
  return true;
};
